import { DateTime, Duration } from 'luxon';

import { EquipmentType, Mode, ServiceLevel } from '@rygen/rygen-ui/model';
import { loadLuxonDateTime } from '@rygen/rygen-ui/util';

import { Carrier } from '../Carrier';
import { RateQuote } from '../rate/RateQuote';

import { SpotMarketExceptionReason } from './SpotMarketExceptionReason';
import {
  isCompletedStatus,
  isResendableStatus,
  SpotMarketStatus,
} from './SpotMarketStatus';

export class SpotMarket {
  id?: number;
  rateShopId?: number;
  carrier?: Carrier;
  status?: SpotMarketStatus;
  mode?: Mode;
  equipmentType?: EquipmentType;
  serviceType?: ServiceLevel;
  createdBy?: string;
  createdDate?: DateTime;
  expirationDuration?: Duration;
  expirationDate?: DateTime;
  requireFuel = false;
  includeCarrierNotes = false;
  includeLocationNotes: boolean = false;
  bids: RateQuote[] = [];
  errors: SpotMarketExceptionReason[] = [];

  constructor(opts?: Partial<SpotMarket>) {
    if (opts?.id) {
      this.id = opts.id;
    }
    if (opts?.rateShopId) {
      this.rateShopId = opts.rateShopId;
    }
    if (opts?.carrier) {
      this.carrier = new Carrier(opts.carrier);
    }
    if (typeof opts?.status === 'string') {
      this.status = SpotMarketStatus[opts.status];
    } else if (opts?.status) {
      this.status = opts.status;
    }
    if (typeof opts?.mode === 'string') {
      this.mode = Mode[opts.mode];
    } else if (opts?.mode) {
      this.mode = opts.mode;
    }
    if (opts?.equipmentType) {
      this.equipmentType = new EquipmentType(opts.equipmentType);
    }
    if (opts?.serviceType) {
      this.serviceType = new ServiceLevel(opts.serviceType);
    }
    if (opts?.createdBy) {
      this.createdBy = opts.createdBy;
    }
    if (opts?.requireFuel) {
      this.requireFuel = opts.requireFuel;
    }
    if (opts?.includeCarrierNotes) {
      this.includeCarrierNotes = opts.includeCarrierNotes;
    }
    if (opts?.includeLocationNotes) {
      this.includeLocationNotes = opts.includeLocationNotes;
    }

    this.createdDate = loadLuxonDateTime(opts?.createdDate);

    if (typeof opts?.expirationDuration === 'string') {
      this.expirationDuration = Duration.fromISO(opts.expirationDuration);
    } else if (opts?.expirationDuration) {
      this.expirationDuration = opts.expirationDuration;
    }

    this.expirationDate = loadLuxonDateTime(opts?.expirationDate);

    if (opts?.bids) {
      this.bids = opts.bids.map((b) => new RateQuote(b));
    }
    if (opts?.errors) {
      this.errors = opts.errors.map((e) => {
        return typeof e === 'string' ? SpotMarketExceptionReason[e] : e;
      });
    }
  }

  get hasErrors() {
    return this.errors.length > 0;
  }

  get isCompleted() {
    return isCompletedStatus(this.status);
  }

  get isRejected() {
    return this.status === SpotMarketStatus.REJECTED;
  }

  set isRejected(newVal: boolean) {
    if (newVal === true) {
      this.status = SpotMarketStatus.REJECTED;
    }
  }

  get isExpired() {
    return this.status === SpotMarketStatus.EXPIRED;
  }

  get isClosed() {
    return this.status === SpotMarketStatus.CLOSED;
  }

  get isSending() {
    return this.status === SpotMarketStatus.SENDING;
  }

  get isFailed() {
    return this.status === SpotMarketStatus.FAILED;
  }

  get isCancelled() {
    return this.status === SpotMarketStatus.CANCELLED;
  }

  get isCancellable() {
    return !this.isCompleted && !this.isSending;
  }

  get isResendable() {
    return isResendableStatus(this.status);
  }
}
